import React, { Fragment, useState } from "react";
import { useController, Controller } from "react-hook-form";
import { CCollapse } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import Dropdown from "../../../../components/multiSelectDropdown/multiSelectDropdown";
import ToggleableButton from "../../../../components/toggleableButton/toggleableButton";

export const DataPermissions = ({
  allPermissions,
  dailyEmailsPermissions,
  control,
  register,
  canChangeEmails,
  canChangePermissions,
  canUpdateCorporateReports,
  canSendInactiveUsersEmail,
  setValue,
  watch,
  canChangeRole,
  orgRoles,
  linkUsersToSharePermissions,
  watchedValues: { isMultiOrg, canAssignRole, isCorporate, isCorporateReporting }
}) => {
  const permissionsLocked = !canChangePermissions;
  const customPermission = canSendInactiveUsersEmail
    ? { id: "send_inactive_users_email", name: "Send Inactive Users Email" }
    : null;

  const watchedValue = watch(customPermission?.id);

  const { field: restPermissions } = useController({ control, name: "permission_ids" });
  const { field: corporatePermissions } = useController({ control, name: "corporate_report_ids" });

  const [permissionTypeKey, setPermissionTypeKey] = useState("");

  const [currentGroupName, setCurrentGroupName] = useState("");

  const onPermissionTypeClick = (type) => {
    setPermissionTypeKey(type);

    if (type === permissionTypeKey) {
      setPermissionTypeKey("");
      setCurrentGroupName("");
    }
  };

  const onGroupPermissionClick = (e) => {
    let targetText = e.target.innerText;

    let label = e.target.parentElement.dataset.label;
    let groupName = label ? label + "-" + targetText : targetText;

    if (groupName === currentGroupName) {
      setCurrentGroupName("");
      return;
    }

    setCurrentGroupName(groupName);
  };

  const onPermissionClick = (e, state) => {
    e.stopPropagation();
    let checked = e.target.checked;
    let val = +e.target.value;

    if (!checked) {
      delete state.value[val];
      state.onChange(state.value);
      return;
    }

    state.onChange({ ...state.value, [val]: checked });
  };

  const handleCustomControl = (checked) => {
    if (customPermission) {
      setValue(customPermission.id, checked, { shouldDirty: true });
    }
  };

  const onAllClick = ({ e, groupParent, options, state }) => {
    let currentPermissions = options.map((p) => p.id);
    let checked = e.target.checked;

    if (groupParent === "notifications_emails_goals") {
      handleCustomControl(checked);
    }

    if (checked) {
      let ids = currentPermissions.reduce((acc, val) => ({ ...acc, [val]: true }), {});
      state.onChange({ ...state.value, ...ids });
    } else {
      for (let key in state.value) {
        if (currentPermissions.includes(+key)) {
          delete state.value[key];
        }
      }

      state.onChange(state.value);
    }
  };

  const isAllChecked = (options, state) => {
    let allPermissionIdsPerGroup = options.map((permission) => permission.id);
    return allPermissionIdsPerGroup.every((id) => state.value[id]);
  };

  const areAllEmailsChecked = (options, state) => {
    const isChecked = isAllChecked(options, state);
    return isChecked && (!customPermission || watchedValue);
  };

  const handleStyles = (permission, state) => {
    let isSeparate = permission.name === "Separate emails for each location";

    return {
      display: !isSeparate ? "block" : state.value[dailyEmailsPermissions[0].id] ? "block" : "none",
      marginLeft: isSeparate ? "20px" : "0px"
    };
  };

  const renderInnerControls = ({ id, groupParent, group, options, state }) => {
    return (
      <>
        {groupParent === "location_permissions" && (
          <p style={{ fontWeight: "bold" }}>
            Selected Locations: {options.filter((option) => state.value[option.id]).length}
          </p>
        )}
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ paddingLeft: "40px", paddingBottom: "15px", width: "100%" }}
        >
          <div className="form-check" key={group}>
            <>
              <input
                className="form-check-input"
                type="checkbox"
                value={group}
                id={id ? id : group}
                onChange={(e) => onAllClick({ e, groupParent, options, state })}
                checked={
                  groupParent == "notifications_emails_goals"
                    ? areAllEmailsChecked(options, state)
                    : isAllChecked(options, state)
                }
              />
              <label className="form-check-label" htmlFor={id ? id : group}>
                Check All / Uncheck All
              </label>
            </>
          </div>

          {options.map((permission) => {
            return (
              <div
                className="form-check"
                key={permission.id}
                style={handleStyles(permission, state)}
              >
                <>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value={permission.id}
                    id={permission.id}
                    onChange={(e) => onPermissionClick(e, state)}
                    checked={state.value[permission.id] ?? false}
                  />
                  <label className="form-check-label" htmlFor={permission.id}>
                    {permission.name}
                  </label>
                </>
              </div>
            );
          })}

          {groupParent === "notifications_emails_goals" && renderCustomControls()}
        </div>
      </>
    );
  };

  const renderCustomControls = () => (
    <div onClick={(e) => e.stopPropagation()}>
      {customPermission && (
        <div className="form-check">
          <>
            <input
              {...register(customPermission.id)}
              id={customPermission.id}
              className="form-check-input"
              type="checkbox"
            />
            <label className="form-check-label" htmlFor={customPermission.id}>
              {customPermission.name}
            </label>
          </>
        </div>
      )}
    </div>
  );

  const renderNestedGroups = (groupParent, hideInnerCaption, state) =>
    Object.keys(allPermissions[groupParent]).map((parent) => {
      let isParent = !Array.isArray(allPermissions[groupParent][parent]);

      if (isParent) {
        return (
          <div key={parent} data-label={parent}>
            <div style={{ marginLeft: "20px" }}>{parent}</div>

            {Object.keys(allPermissions[groupParent][parent]).map((group) => {
              let id = parent + "-" + group;
              return (
                <Fragment key={id}>
                  <div className="group__item" style={{ marginLeft: "20px" }}>
                    {group}
                  </div>
                  <CCollapse show={id === currentGroupName}>
                    {renderInnerControls({
                      id,
                      group,
                      groupParent,
                      options: allPermissions[groupParent][parent][group],
                      state
                    })}
                  </CCollapse>
                </Fragment>
              );
            })}
          </div>
        );
      }

      return hideInnerCaption ? (
        <Fragment key={parent}>
          {renderInnerControls({
            group: parent,
            groupParent,
            options: allPermissions[groupParent][parent],
            state
          })}
        </Fragment>
      ) : (
        <Fragment key={parent}>
          <div className="group__item" style={{ marginLeft: "20px" }}>
            {parent}
          </div>
          <CCollapse show={parent === currentGroupName}>
            {renderInnerControls({
              group: parent,
              groupParent,
              options: allPermissions[groupParent][parent],
              state
            })}
          </CCollapse>
        </Fragment>
      );
    });

  const renderGroups = (groupParent, hideInnerCaption) => {
    let state =
      groupParent === "reporting_portal_permissions" ? corporatePermissions : restPermissions;

    return (
      <div onClick={(e) => onGroupPermissionClick(e)}>
        <CCollapse show={!permissionsLocked && groupParent === permissionTypeKey}>
          {Array.isArray(allPermissions[groupParent])
            ? renderInnerControls({
                group: groupParent,
                groupParent,
                options: allPermissions[groupParent],
                state
              })
            : renderNestedGroups(groupParent, hideInnerCaption, state)}
        </CCollapse>
      </div>
    );
  };

  const renderPermissionGroup = (groupParent, label, hideInnerCaption) => (
    <>
      <div
        className="group__item"
        style={permissionsLocked ? { cursor: "not-allowed", opacity: 0.6 } : null}
        onClick={() => !permissionsLocked && onPermissionTypeClick(groupParent)}
        data-tooltip-id={permissionsLocked ? "permissionsLockedTooltip" : null}
        data-tooltip-content={permissionsLocked ? "Save changes to edit permissions" : null}
      >
        {label}
      </div>
      {renderGroups(groupParent, hideInnerCaption)}
    </>
  );

  return (
    <>
      <>
        <div className="light group">
          <div className="admin-users__section">
            {canChangeRole && (
              <>
                <div className="admin-users__dropdown">
                  <p>Role</p>
                  <Controller
                    control={control}
                    name="role_id"
                    render={({ field: { onChange, value } }) => {
                      return (
                        <Dropdown
                          height={275}
                          haveOnSelectChange
                          options={orgRoles}
                          selectedFilters={value}
                          numberDisplayed={0}
                          onSelectChange={(data) => onChange(data)}
                        />
                      );
                    }}
                  />
                </div>
                <div className="switch">
                  <p className="switch__title">Assign Role</p>
                  <Controller
                    control={control}
                    name="can_assign_role"
                    render={({ field: { onChange, value } }) => {
                      return (
                        <div
                          className={`switch__content ${value ? "switch__content--active" : ""}`}
                        >
                          <ToggleableButton
                            isRadio
                            title="can_assign_role"
                            isActive={value}
                            onClick={(isActive) => onChange(isActive)}
                          />
                        </div>
                      );
                    }}
                  />
                </div>
                {canAssignRole ? (
                  <div className="admin-users__dropdown">
                    <p>
                      {isMultiOrg
                        ? "User can assign any role from user organizations"
                        : "User can assign these roles"}
                    </p>
                    {!isMultiOrg && (
                      <Controller
                        control={control}
                        name="available_for_assign_role_ids"
                        render={({ field: { onChange, value } }) => {
                          return (
                            <Dropdown
                              height={275}
                              haveOnSelectChange
                              options={orgRoles}
                              selectedFilters={value}
                              numberDisplayed={0}
                              onSelectChange={(data) => onChange(data)}
                            />
                          );
                        }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="admin-users__empty" />
                )}
              </>
            )}
          </div>
          <div className="admin-users__section">
            <div className="switch">
              <p className="switch__title">Reporting Portal</p>
              <Controller
                control={control}
                name="corporate_reporting"
                render={({ field: { onChange, value } }) => {
                  return (
                    <div className={`switch__content ${value ? "switch__content--active" : ""}`}>
                      <ToggleableButton
                        isRadio
                        title="corporate_reporting"
                        isActive={value}
                        onClick={(isActive) => onChange(isActive)}
                      />
                    </div>
                  );
                }}
              />
            </div>

            {isCorporateReporting && !isCorporate && !isMultiOrg ? (
              <div className="admin-users__dropdown">
                <p>Link users to share permissions</p>
                <Controller
                  control={control}
                  name="user_group_user_ids"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Dropdown
                        includeSelectAll
                        enableOnly
                        height={275}
                        haveOnSelectChange
                        options={linkUsersToSharePermissions}
                        selectedFilters={value}
                        numberDisplayed={0}
                        onSelectChange={(data) => onChange(data)}
                      />
                    );
                  }}
                />
              </div>
            ) : (
              <div className="admin-users__empty" />
            )}
            <div className="admin-users__empty" />
          </div>
          {renderPermissionGroup("charts_permissions", "Chart Permissions")}
          {canUpdateCorporateReports &&
            renderPermissionGroup("reporting_portal_permissions", "Reporting Portal Permissions")}

          {renderPermissionGroup("admin_permissions", "Admin Permissions")}

          {canChangeEmails &&
            renderPermissionGroup(
              "notifications_emails_goals",
              "Notifications / Emails / Goals",
              true
            )}

          {permissionsLocked && <Tooltip id="permissionsLockedTooltip" place="top" />}
        </div>
      </>
    </>
  );
};
